import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { formatIsoDateDisplay } from '@/shared/lib/datetime'
import { isHouseholdPartnerRelation } from '@/shared/lib/householdPartner'
import {
  beneficiaryRelationLabel,
  type BeneficiaryOption,
} from '@/shared/schemas/beneficiaryDisplay.schema'

type BeneficiarySelectorProps = {
  beneficiaries: BeneficiaryOption[]
  selectedIds: string[]
  /** Absolute max selections; omit/`null` = no numeric hint (still enforces partner rule). */
  maxSelectable: number | null
  onChange: (ids: string[]) => void
  disabled?: boolean
  idPrefix?: string
  manageProfileHref?: string
  /** Optional help below the legend (e.g. blended/adopted children). */
  helpText?: string
}

export function BeneficiarySelector({
  beneficiaries,
  selectedIds,
  maxSelectable,
  onChange,
  disabled = false,
  idPrefix = 'beneficiary',
  manageProfileHref = '/me/beneficiaries',
  helpText,
}: BeneficiarySelectorProps) {
  const { t } = useTranslation()

  if (beneficiaries.length === 0) {
    return (
      <div className="space-y-2" data-testid="beneficiary-selector-empty">
        <p className="text-sm text-muted-foreground">{t('shared.beneficiary_selector.empty')}</p>
        <Link to={manageProfileHref} className="text-sm text-primary underline-offset-4 hover:underline">
          {t('shared.beneficiary_selector.manage_link')}
        </Link>
      </div>
    )
  }

  const selectedPartnerId = beneficiaries.find(
    (b) => selectedIds.includes(b.id) && isHouseholdPartnerRelation(b.relation),
  )?.id

  const effectiveMax = maxSelectable ?? Number.POSITIVE_INFINITY

  const toggle = (id: string) => {
    if (disabled) {
      return
    }
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((value) => value !== id))
      return
    }
    if (selectedIds.length >= effectiveMax) {
      return
    }
    const row = beneficiaries.find((b) => b.id === id)
    if (
      row &&
      isHouseholdPartnerRelation(row.relation) &&
      selectedPartnerId !== undefined &&
      selectedPartnerId !== id
    ) {
      return
    }
    onChange([...selectedIds, id])
  }

  return (
    <fieldset className="space-y-2" disabled={disabled} data-testid="beneficiary-selector">
      <legend className="text-sm font-medium text-foreground">{t('shared.beneficiary_selector.legend')}</legend>
      {maxSelectable !== null ? (
        <p className="text-xs text-muted-foreground">
          {t('shared.beneficiary_selector.hint', { count: maxSelectable })}
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">{t('shared.beneficiary_selector.hint_unlimited')}</p>
      )}
      {helpText ? <p className="text-xs text-muted-foreground">{helpText}</p> : null}
      {selectedPartnerId !== undefined ? (
        <p className="text-xs text-muted-foreground">{t('shared.beneficiary_selector.one_partner_hint')}</p>
      ) : null}
      <ul className="space-y-2">
        {beneficiaries.map((row) => {
          const inputId = `${idPrefix}-${row.id}`
          const checked = selectedIds.includes(row.id)
          const atLimit = !checked && selectedIds.length >= effectiveMax
          const partnerBlocked =
            !checked &&
            isHouseholdPartnerRelation(row.relation) &&
            selectedPartnerId !== undefined &&
            selectedPartnerId !== row.id
          const rowDisabled = disabled || atLimit || partnerBlocked

          return (
            <li key={row.id}>
              <label
                htmlFor={inputId}
                className={`flex min-h-11 items-start gap-3 rounded-md border px-3 py-2 text-sm ${
                  checked ? 'border-primary bg-primary/5' : 'border-border bg-background'
                } ${rowDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
              >
                <input
                  id={inputId}
                  type="checkbox"
                  className="mt-0.5 size-5 shrink-0 accent-primary"
                  checked={checked}
                  disabled={rowDisabled}
                  aria-label={`${row.prenom} ${row.nom}`}
                  onChange={() => toggle(row.id)}
                />
                <span>
                  <span className="font-medium text-foreground">
                    {row.prenom} {row.nom}
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {row.date_naissance ? `${formatIsoDateDisplay(row.date_naissance)} · ` : ''}
                    {beneficiaryRelationLabel(t, row.relation)}
                    {row.relation === 'other' && row.relation_other ? ` - ${row.relation_other}` : ''}
                  </span>
                </span>
              </label>
            </li>
          )
        })}
      </ul>
    </fieldset>
  )
}
